import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Checkbox,
  Container,
  FormControlLabel,
  Paper,
  Stack,
  Tab,
  TextField,
  Tabs,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import useAuth from "../hooks/useAuth";
import { buildPain008Xml } from "../components/sepa/SepaUtils";
import { useGetBankingAccountsQuery } from "../features/banking/bankingApiSlice";
import {
  useCreateShopOrderMutation,
  useCreateShopProductMutation,
  useDeleteShopOrderMutation,
  useDeleteShopProductMutation,
  useDownloadShopMonthlyReportMutation,
  useExportShopOrdersSepaMutation,
  useGetAdminShopOrdersQuery,
  useGetShopAccessQuery,
  useGetShopMonthlyReportQuery,
  useGetShopProductsQuery,
  useReorderShopProductsMutation,
  useUploadShopProductImagesMutation,
  useUpdateShopOrderStatusMutation,
  useUpdateShopProductMutation,
} from "../features/shop/shopApiSlice";
import { useDispatch } from "react-redux";
import { setDialog } from "../features/dialog/dialogSlice";
import { useGetMembersQuery } from "../features/members/membersApiSlice";

import {
  EMPTY_ARRAY,
  CART_STORAGE_KEY,
  getVariantStock,
  getUniqueProductCategories,
  isVariantInStock,
  ORDER_STATUS_OPTIONS,
} from "../sections/@dashboard/shop/shopUtils";

import ProductFormDialog from "../sections/@dashboard/shop/ProductFormDialog";
import ShopCatalogTab from "../sections/@dashboard/shop/ShopCatalogTab";
import ShopProductsTab from "../sections/@dashboard/shop/ShopProductsTab";
import ShopOrdersTab from "../sections/@dashboard/shop/ShopOrdersTab";
import ShopReportsTab from "../sections/@dashboard/shop/ShopReportsTab";
import ShopCheckoutDialog from "../sections/@dashboard/shop/ShopCheckoutDialog";
import ShopCartFab from "../sections/@dashboard/shop/ShopCartFab";
import ShopCartDrawer from "../sections/@dashboard/shop/ShopCartDrawer";
import ShopProductDetailPage from "../sections/@dashboard/shop/ShopProductDetailPage";

const buildShopProductPath = (productId, variantKey = null) => {
  const encodedProductId = encodeURIComponent(String(productId || "").trim());
  if (!encodedProductId) return "/dashboard/shop";
  const basePath = `/dashboard/shop/produkt/${encodedProductId}`;
  if (!variantKey) return basePath;
  return `${basePath}/${encodeURIComponent(String(variantKey))}`;
};

const decodeRouteParam = (value) => {
  if (!value) return null;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const normalizeShopTab = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const DEFAULT_ORDER_STATUS_FILTER = ORDER_STATUS_OPTIONS.filter(
  (status) => status !== "fulfilled"
);
const ADMIN_ORDER_MEMBER_STORAGE_KEY = "ma-shop-admin-order-member";

const buildMemberDisplayName = (member) => {
  const fullName = `${member?.firstName || ""} ${
    member?.lastName || ""
  }`.trim();
  if (fullName) return fullName;
  if (member?.memberNumber) return `Mitglied ${member.memberNumber}`;
  return "Unbekanntes Mitglied";
};

const buildContractDisplayName = (contract) => {
  const owner = String(contract?.owner || "").trim();
  if (owner) return owner;
  const fullName = `${contract?.firstName || ""} ${
    contract?.lastName || ""
  }`.trim();
  return fullName || "Unbekannter Vertrag";
};

// ─────────────────────────────────────────────────────────────────────────────
// ShopPage — Orchestrator
// ─────────────────────────────────────────────────────────────────────────────
export default function ShopPage() {
  const dispatch = useDispatch();
  const { isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { productId: routeProductIdParam, variantKey: routeVariantParam } =
    useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastCheckoutTargetKeyRef = useRef(null);

  // ── Tab & view state ──
  const [activeTab, setActiveTab] = useState("catalog");
  const [memberPreview, setMemberPreview] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [checkoutReviewOpen, setCheckoutReviewOpen] = useState(false);
  const [checkoutFulfillmentType, setCheckoutFulfillmentType] =
    useState("shipping");
  const [runningAdminAction, setRunningAdminAction] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [togglingProductId, setTogglingProductId] = useState(null);
  const [selectedAdminOrderTargetId, setSelectedAdminOrderTargetId] = useState(
    () => {
      try {
        return localStorage.getItem(ADMIN_ORDER_MEMBER_STORAGE_KEY) || "";
      } catch {
        return "";
      }
    }
  );

  // ── Cart with localStorage persistence ──
  const [cartItems, setCartItems] = useState(() => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      if (cartItems.length) {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
      } else {
        localStorage.removeItem(CART_STORAGE_KEY);
      }
    } catch {
      /* quota exceeded – ignore */
    }
    // Notify header cart button of count change
    window.dispatchEvent(new Event("ma-cart-updated"));
  }, [cartItems]);

  // ── Admin order filters ──
  const [adminMonth, setAdminMonth] = useState(dayjs().format("YYYY-MM"));
  const [orderStatusFilter, setOrderStatusFilter] = useState(
    DEFAULT_ORDER_STATUS_FILTER
  );
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  // ── RTK Query hooks ──
  const { data: shopAccess, isLoading: loadingAccess } =
    useGetShopAccessQuery();
  const { data: products = EMPTY_ARRAY, isLoading: loadingProducts } =
    useGetShopProductsQuery();
  const { data: bankingAccounts = EMPTY_ARRAY } = useGetBankingAccountsQuery();
  const {
    data: members = EMPTY_ARRAY,
    isLoading: loadingMembers,
    isError: hasMembersError,
  } = useGetMembersQuery(undefined, {
    skip: !Boolean(shopAccess?.canManageOperations),
  });

  const canManageProducts =
    Boolean(shopAccess?.canManageProducts) && !memberPreview;
  const canManageOperationsBase = Boolean(shopAccess?.canManageOperations);
  const canManageOperations = canManageOperationsBase && !memberPreview;
  const showAdminPreviewToggle = isAdmin || isSuperAdmin;
  const hasSelfContractContext = Boolean(
    shopAccess?.contractContext?.contractId
  );
  const isMemberCatalogView = !canManageProducts;
  const isAdminCheckoutMode = canManageOperationsBase && !memberPreview;

  // ── URL-derived product/variant state (route params) ──
  const productIdFromUrl = decodeRouteParam(routeProductIdParam);
  const variantKeyFromUrl = decodeRouteParam(routeVariantParam);
  const isProductRoute = Boolean(productIdFromUrl);
  const showPageHeader = !isProductRoute;

  // selectedProduct is derived from URL + loaded products list
  const selectedProduct = useMemo(() => {
    if (!productIdFromUrl || loadingProducts) return null;
    return products.find((p) => String(p._id) === productIdFromUrl) || null;
  }, [productIdFromUrl, products, loadingProducts]);

  const statusFilterQueryValue = useMemo(
    () =>
      Array.isArray(orderStatusFilter)
        ? orderStatusFilter.length
          ? orderStatusFilter.join(",")
          : "__none__"
        : String(orderStatusFilter || ""),
    [orderStatusFilter]
  );

  const {
    data: adminOrders = EMPTY_ARRAY,
    isLoading: loadingAdminOrders,
    refetch: refetchAdminOrders,
  } = useGetAdminShopOrdersQuery(
    { month: adminMonth, status: statusFilterQueryValue },
    { skip: !canManageOperations }
  );

  const {
    data: monthlyReport,
    isLoading: loadingMonthlyReport,
    refetch: refetchMonthlyReport,
  } = useGetShopMonthlyReportQuery(
    { month: adminMonth },
    { skip: !canManageOperations }
  );

  const [createShopOrder, { isLoading: creatingOrder }] =
    useCreateShopOrderMutation();
  const [createShopProduct, { isLoading: creatingProduct }] =
    useCreateShopProductMutation();
  const [updateShopProduct, { isLoading: updatingProduct }] =
    useUpdateShopProductMutation();
  const [uploadShopProductImages, { isLoading: uploadingImages }] =
    useUploadShopProductImagesMutation();
  const [deleteShopProduct, { isLoading: deletingProduct }] =
    useDeleteShopProductMutation();
  const [updateShopOrderStatus] = useUpdateShopOrderStatusMutation();
  const [deleteShopOrder] = useDeleteShopOrderMutation();
  const [exportShopOrdersSepa, { isLoading: exportingSepa }] =
    useExportShopOrdersSepaMutation();
  const [downloadShopMonthlyReport, { isLoading: downloadingReport }] =
    useDownloadShopMonthlyReportMutation();
  const [reorderShopProducts] = useReorderShopProductsMutation();

  // ── Tabs ──
  const tabs = useMemo(() => {
    const next = [{ key: "catalog", label: "Katalog" }];
    if (canManageProducts)
      next.push({ key: "products", label: "Produkte verwalten" });
    if (canManageOperations) {
      next.push({ key: "orders", label: "Bestellungen & SEPA" });
      next.push({ key: "reports", label: "Monatsreport" });
    }
    return next;
  }, [canManageProducts, canManageOperations]);
  const showTabNavigation = !isProductRoute && tabs.length > 1;

  const adminOrderTargets = useMemo(() => {
    if (!Array.isArray(members)) return EMPTY_ARRAY;

    return members
      .map((member) => {
        const contract = member?.contractRef;
        const contractId = String(contract?._id || "");
        if (!contractId) return null;

        const memberName = buildMemberDisplayName(member);
        const contractOwner = buildContractDisplayName(contract);
        const memberNumber = String(member?.memberNumber || "").trim();
        const siteName = String(
          member?.standortRef?.name ||
            member?.standortRef?.siteName ||
            member?.standortRef?.standortName ||
            ""
        ).trim();
        const searchLabel = [
          memberName,
          memberNumber ? `Nr. ${memberNumber}` : "",
          contractOwner && contractOwner !== memberName
            ? `Vertrag ${contractOwner}`
            : "Vertrag vorhanden",
          siteName,
        ]
          .filter(Boolean)
          .join(" · ");

        return {
          memberId: String(member?._id || ""),
          memberName,
          memberNumber,
          contractId,
          contractOwner,
          siteName,
          searchLabel,
        };
      })
      .filter(Boolean)
      .sort((left, right) =>
        left.memberName.localeCompare(right.memberName, "de", {
          sensitivity: "base",
        })
      );
  }, [members]);

  const selectedAdminOrderTarget = useMemo(
    () =>
      adminOrderTargets.find(
        (target) => target.memberId === selectedAdminOrderTargetId
      ) || null,
    [adminOrderTargets, selectedAdminOrderTargetId]
  );

  const canCheckout = isAdminCheckoutMode
    ? Boolean(selectedAdminOrderTarget?.contractId)
    : hasSelfContractContext;
  const checkoutContractId = isAdminCheckoutMode
    ? String(selectedAdminOrderTarget?.contractId || "")
    : String(shopAccess?.contractContext?.contractId || "");
  const checkoutContractOwner = isAdminCheckoutMode
    ? selectedAdminOrderTarget?.contractOwner || ""
    : shopAccess?.contractContext?.contractOwner || "";
  const checkoutMemberName = isAdminCheckoutMode
    ? selectedAdminOrderTarget?.memberName || ""
    : "";
  const checkoutDisabledMessage = isAdminCheckoutMode
    ? hasMembersError
      ? "Die Mitgliederliste konnte nicht geladen werden. Bestellungen für Mitglieder sind aktuell nicht möglich."
      : "Wähle zuerst ein Mitglied aus, damit die Bestellung dem richtigen Vertrag zugeordnet wird."
    : "Dein Konto hat keinen verknüpften Vertrag. Bestellungen sind aktuell nicht möglich.";
  const checkoutContextNote =
    isAdminCheckoutMode && selectedAdminOrderTarget
      ? `Bestellung für ${
          selectedAdminOrderTarget.memberName
        }. Abrechnung über ${
          selectedAdminOrderTarget.contractOwner || "den verknüpften Vertrag"
        }.`
      : "";
  const checkoutTargetKey = useMemo(() => {
    if (loadingAccess) return null;
    return isAdminCheckoutMode
      ? `admin:${selectedAdminOrderTargetId || ""}`
      : `self:${shopAccess?.contractContext?.contractId || ""}`;
  }, [
    isAdminCheckoutMode,
    loadingAccess,
    selectedAdminOrderTargetId,
    shopAccess?.contractContext?.contractId,
  ]);

  useEffect(() => {
    if (!tabs.some((t) => t.key === activeTab)) setActiveTab("catalog");
  }, [activeTab, tabs]);

  useEffect(() => {
    if (isProductRoute && activeTab !== "catalog") {
      setActiveTab("catalog");
    }
  }, [activeTab, isProductRoute]);

  // Sync selectedOrderIds with available orders
  useEffect(() => {
    setSelectedOrderIds((prev) => {
      if (!Array.isArray(adminOrders) || !adminOrders.length)
        return prev.length ? [] : prev;
      const validIds = new Set(adminOrders.map((o) => o._id));
      const filtered = prev.filter((id) => validIds.has(id));
      return filtered.length !== prev.length ? filtered : prev;
    });
  }, [adminOrders]);

  useEffect(() => {
    if (checkoutReviewOpen && !cartItems.length) setCheckoutReviewOpen(false);
  }, [checkoutReviewOpen, cartItems]);

  useEffect(() => {
    try {
      if (selectedAdminOrderTargetId) {
        localStorage.setItem(
          ADMIN_ORDER_MEMBER_STORAGE_KEY,
          selectedAdminOrderTargetId
        );
      } else {
        localStorage.removeItem(ADMIN_ORDER_MEMBER_STORAGE_KEY);
      }
    } catch {
      // ignore localStorage write errors
    }
  }, [selectedAdminOrderTargetId]);

  useEffect(() => {
    if (loadingMembers || hasMembersError || !selectedAdminOrderTargetId)
      return;
    const targetStillExists = adminOrderTargets.some(
      (target) => target.memberId === selectedAdminOrderTargetId
    );
    if (!targetStillExists) {
      setSelectedAdminOrderTargetId("");
    }
  }, [
    adminOrderTargets,
    hasMembersError,
    loadingMembers,
    selectedAdminOrderTargetId,
  ]);

  useEffect(() => {
    if (!checkoutTargetKey) return;

    if (lastCheckoutTargetKeyRef.current === null) {
      lastCheckoutTargetKeyRef.current = checkoutTargetKey;
      return;
    }

    if (lastCheckoutTargetKeyRef.current === checkoutTargetKey) return;

    if (cartItems.length) {
      setCartItems([]);
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Warenkorb geleert",
          data: {
            successText: isAdminCheckoutMode
              ? "Der Warenkorb wurde geleert, damit keine Positionen versehentlich dem falschen Mitglied bzw. Vertrag zugeordnet werden."
              : "Der Warenkorb wurde geleert, weil sich der Bestellkontext geändert hat.",
          },
        })
      );
    }

    lastCheckoutTargetKeyRef.current = checkoutTargetKey;
  }, [cartItems.length, checkoutTargetKey, dispatch, isAdminCheckoutMode]);

  // Open cart drawer when navigated from the header cart button
  useEffect(() => {
    if (searchParams.get("openCart") === "1") {
      setCartDrawerOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("openCart");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (isProductRoute || loadingAccess) return;

    const requestedTab = normalizeShopTab(searchParams.get("tab"));
    if (!requestedTab) return;

    const hasRequestedTab = tabs.some((tab) => tab.key === requestedTab);
    if (!hasRequestedTab) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
      return;
    }

    if (activeTab !== requestedTab) {
      setActiveTab(requestedTab);
    }
  }, [
    activeTab,
    isProductRoute,
    loadingAccess,
    searchParams,
    setSearchParams,
    tabs,
  ]);

  // ── Derived cart data ──
  const cartTotal = useMemo(
    () =>
      cartItems.reduce(
        (s, i) => s + Number(i.unitPrice || 0) * Number(i.quantity || 0),
        0
      ),
    [cartItems]
  );
  const cartTotalItems = cartItems.reduce(
    (s, i) => s + Number(i.quantity || 0),
    0
  );
  const catalogProducts = useMemo(
    () => products.filter((p) => p?.isActive !== false),
    [products]
  );
  const productCategoryOptions = useMemo(
    () => getUniqueProductCategories(products),
    [products]
  );
  const variantInventoryByKey = useMemo(() => {
    const inventory = new Map();
    products.forEach((product) => {
      const productId = String(product?._id || "");
      if (!productId) return;
      (product?.variants || []).forEach((variant) => {
        const variantKey = String(variant?.key || "");
        if (!variantKey) return;
        inventory.set(`${productId}::${variantKey}`, {
          stock: getVariantStock(variant),
          isInStock: isVariantInStock(variant),
          showStockToMembers: Boolean(product?.showVariantStockToMembers),
        });
      });
    });
    return inventory;
  }, [products]);
  const cartVariantInventoryByKey = useMemo(() => {
    const result = {};
    variantInventoryByKey.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }, [variantInventoryByKey]);

  useEffect(() => {
    setCartItems((prev) => {
      let changed = false;
      const nextItems = [];

      prev.forEach((item) => {
        const key = `${item.productId}::${item.variantKey}`;
        const inventory = variantInventoryByKey.get(key);
        const inStock =
          typeof inventory?.isInStock === "boolean"
            ? inventory.isInStock
            : true;
        const stockLimit = Number.isInteger(inventory?.stock)
          ? inventory.stock
          : null;
        const currentQty = Math.max(1, Number(item.quantity) || 1);
        const nextQty =
          stockLimit === null ? currentQty : Math.min(currentQty, stockLimit);

        if (!inStock || nextQty < 1) {
          changed = true;
          return;
        }

        const nextItem = {
          ...item,
          quantity: nextQty,
          availableStock: stockLimit,
          isInStock: true,
        };

        if (
          nextQty !== currentQty ||
          item.availableStock !== nextItem.availableStock ||
          item.isInStock !== nextItem.isInStock
        ) {
          changed = true;
        }

        nextItems.push(nextItem);
      });

      return changed ? nextItems : prev;
    });
  }, [variantInventoryByKey]);

  // ── Admin order stats ──
  const adminOrderStats = useMemo(() => {
    const base = {
      total: adminOrders.length,
      created: 0,
      processing: 0,
      approved: 0,
      fulfilled: 0,
      canceled: 0,
      revenue: 0,
    };
    return adminOrders.reduce((acc, o) => {
      acc.revenue += Number(o?.totals?.grandTotal || 0);
      if (o.status === "created") acc.created += 1;
      if (o.status === "processing") acc.processing += 1;
      if (o.status === "approved") acc.approved += 1;
      if (o.status === "fulfilled" || o.status === "exportedSepa")
        acc.fulfilled += 1;
      if (o.status === "canceled") acc.canceled += 1;
      return acc;
    }, base);
  }, [adminOrders]);

  // ── Cart handlers ──
  const handleAddToCart = useCallback(
    (item) => {
      const cartKey = `${item.productId}::${item.variantKey}`;
      const inventory = variantInventoryByKey.get(cartKey);
      const inStock =
        typeof inventory?.isInStock === "boolean" ? inventory.isInStock : true;
      const stockLimit = Number.isInteger(inventory?.stock)
        ? inventory.stock
        : null;
      if (!inStock) return;

      setCartItems((prev) => {
        const idx = prev.findIndex(
          (e) =>
            e.productId === item.productId && e.variantKey === item.variantKey
        );
        const incomingQuantity = Math.max(1, Number(item.quantity) || 1);
        if (idx < 0) {
          const nextQuantity =
            stockLimit === null
              ? incomingQuantity
              : Math.min(incomingQuantity, stockLimit);
          if (nextQuantity < 1) return prev;
          return [
            ...prev,
            {
              ...item,
              quantity: nextQuantity,
              availableStock: stockLimit,
              isInStock: true,
            },
          ];
        }
        return prev.map((e, i) =>
          i === idx
            ? {
                ...e,
                quantity:
                  stockLimit === null
                    ? Number(e.quantity) + incomingQuantity
                    : Math.min(
                        Number(e.quantity) + incomingQuantity,
                        stockLimit
                      ),
                availableStock: stockLimit,
                isInStock: true,
              }
            : e
        );
      });
    },
    [variantInventoryByKey]
  );

  // ── Product detail navigation (route-based) ──
  const handleSelectProduct = useCallback(
    (product) => {
      if (!product?._id) return;
      navigate(buildShopProductPath(product._id), { replace: false });
    },
    [navigate]
  );

  const handleCloseProductPage = useCallback(() => {
    navigate("/dashboard/shop", { replace: false });
  }, [navigate]);

  const handleGoToCart = useCallback(() => {
    navigate("/dashboard/shop", { replace: false });
  }, [navigate]);

  const handleVariantChange = useCallback(
    (variantKey) => {
      if (!productIdFromUrl) return;
      if (!variantKey || variantKey === variantKeyFromUrl) return;
      navigate(buildShopProductPath(productIdFromUrl, variantKey), {
        replace: true,
      });
    },
    [navigate, productIdFromUrl, variantKeyFromUrl]
  );

  const handleTabChange = useCallback(
    (_, nextTab) => {
      setActiveTab(nextTab);

      if (!isProductRoute) {
        const next = new URLSearchParams(searchParams);
        if (nextTab === "catalog") {
          next.delete("tab");
        } else {
          next.set("tab", nextTab);
        }
        setSearchParams(next, { replace: true });
      }

      if (nextTab !== "catalog" && isProductRoute) {
        navigate(`/dashboard/shop?tab=${encodeURIComponent(nextTab)}`, {
          replace: false,
        });
      }
    },
    [isProductRoute, navigate, searchParams, setSearchParams]
  );

  const handleCheckout = () => {
    if (!cartItems.length || !canCheckout) return;
    setCheckoutReviewOpen(true);
  };

  const handleConfirmCheckout = async () => {
    if (!cartItems.length || !canCheckout) return;
    try {
      await createShopOrder({
        lineItems: cartItems.map((item) => ({
          productId: item.productId,
          variantKey: item.variantKey,
          quantity: Number(item.quantity),
        })),
        buyerContractId: checkoutContractId,
        ...(isAdminCheckoutMode && checkoutFulfillmentType === "local"
          ? { fulfillmentType: "local" }
          : {}),
      }).unwrap();
      setCartItems([]);
      setCheckoutReviewOpen(false);
      setCheckoutFulfillmentType("shipping");
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Bestellung erfolgreich",
          data: {
            refetchOnClose: false,
            successText:
              isAdminCheckoutMode && selectedAdminOrderTarget
                ? `Die Bestellung für ${
                    selectedAdminOrderTarget.memberName
                  } wurde angelegt. Die Abrechnung läuft über ${
                    selectedAdminOrderTarget.contractOwner ||
                    "den verknüpften Vertrag"
                  } und erscheint im SEPA-Workflow.`
                : "Deine Bestellung wurde verbindlich übermittelt und wird nun vom Team verarbeitet.",
          },
        })
      );
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Bestellung fehlgeschlagen",
          data: {
            errorText:
              error?.data?.message ||
              "Bestellung konnte nicht erstellt werden.",
          },
        })
      );
    }
  };

  // ── Product CRUD ──
  const handleSaveProduct = async (payload) => {
    try {
      if (payload.id) {
        await updateShopProduct(payload).unwrap();
      } else {
        await createShopProduct(payload).unwrap();
      }
      setProductDialogOpen(false);
      setEditingProduct(null);
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Produkt konnte nicht gespeichert werden",
          data: { errorText: error?.data?.message || "Unbekannter Fehler" },
        })
      );
    }
  };

  const handleUploadProductImages = async (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    const result = await uploadShopProductImages(formData).unwrap();
    return result?.imageUrls?.length ? result.imageUrls : result?.images || [];
  };

  const handleDeleteProduct = async (id) => {
    try {
      await deleteShopProduct(id).unwrap();
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Produkt konnte nicht gelöscht werden",
          data: { errorText: error?.data?.message || "Unbekannter Fehler" },
        })
      );
    }
  };

  const handleToggleProductActive = async (product) => {
    setTogglingProductId(product._id);
    try {
      await updateShopProduct({
        id: product._id,
        isActive: !product.isActive,
      }).unwrap();
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Status konnte nicht geändert werden",
          data: { errorText: error?.data?.message || "Unbekannter Fehler" },
        })
      );
    } finally {
      setTogglingProductId(null);
    }
  };

  const handleReorderProducts = useCallback(
    (orderedIds) => {
      reorderShopProducts({ orderedIds });
    },
    [reorderShopProducts]
  );

  // ── Order handlers ──
  const handleOrderStatusChange = async (orderId, status) => {
    try {
      await updateShopOrderStatus({ id: orderId, status }).unwrap();
      refetchAdminOrders();
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Status konnte nicht aktualisiert werden",
          data: { errorText: error?.data?.message || "Unbekannter Fehler" },
        })
      );
    }
  };

  const handleCancelOrders = async (orderIds, successMessage) => {
    const unique = Array.from(new Set(orderIds.filter(Boolean)));
    if (!unique.length) return;
    setRunningAdminAction(true);
    try {
      const results = await Promise.allSettled(
        unique.map((id) =>
          updateShopOrderStatus({ id, status: "canceled" }).unwrap()
        )
      );
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length) throw failed[0]?.reason;
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Bestellung storniert",
          data: { successText: successMessage },
        })
      );
      setSelectedOrderIds((prev) => prev.filter((id) => !unique.includes(id)));
      refetchAdminOrders();
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Stornierung fehlgeschlagen",
          data: {
            errorText:
              error?.data?.message ||
              "Bestellungen konnten nicht storniert werden.",
          },
        })
      );
    } finally {
      setRunningAdminAction(false);
    }
  };

  const handleDeleteOrders = async (orderIds, successMessage) => {
    const unique = Array.from(new Set(orderIds.filter(Boolean)));
    if (!unique.length) return;
    setRunningAdminAction(true);
    try {
      const results = await Promise.allSettled(
        unique.map((id) => deleteShopOrder(id).unwrap())
      );
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length) throw failed[0]?.reason;
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Bestellung gelöscht",
          data: { successText: successMessage },
        })
      );
      setSelectedOrderIds((prev) => prev.filter((id) => !unique.includes(id)));
      refetchAdminOrders();
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Löschen fehlgeschlagen",
          data: {
            errorText:
              error?.data?.message ||
              "Bestellungen konnten nicht gelöscht werden.",
          },
        })
      );
    } finally {
      setRunningAdminAction(false);
    }
  };

  const handleBulkStatusChange = async (orderIds, status, successMessage) => {
    const unique = Array.from(new Set(orderIds.filter(Boolean)));
    if (!unique.length) return;
    setRunningAdminAction(true);
    try {
      const results = await Promise.allSettled(
        unique.map((id) =>
          updateShopOrderStatus({ id, status }).unwrap()
        )
      );
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length) throw failed[0]?.reason;
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Status aktualisiert",
          data: { successText: successMessage },
        })
      );
      setSelectedOrderIds((prev) => prev.filter((id) => !unique.includes(id)));
      refetchAdminOrders();
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Statusänderung fehlgeschlagen",
          data: {
            errorText:
              error?.data?.message ||
              "Status konnte nicht aktualisiert werden.",
          },
        })
      );
    } finally {
      setRunningAdminAction(false);
    }
  };

  const handleExportSepa = async (exportableIds) => {
    if (!exportableIds?.length) return;
    try {
      const response = await exportShopOrdersSepa({
        orderIds: exportableIds,
        referenceMonth: adminMonth,
      }).unwrap();

      const collectionDate = dayjs().add(2, "day").format("YYYY-MM-DD");
      const xml = buildPain008Xml({
        creditor: response.creditor,
        collectionDate,
        payments: (response.payments || []).map((p) => ({
          debtorName: p.debtorName,
          iban: p.iban,
          bic: p.bic,
          amount: p.amount,
          mandateId: p.mandateId,
          mandateDate: p.mandateDate,
          remittance: p.remittance,
        })),
      });

      if (!xml) return;

      const blob = new Blob([xml], { type: "text/xml;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `SHOP_SEPA_${
        response.referenceMonth || dayjs().format("YYYY-MM")
      }_${response.exportId}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "SEPA Export erfolgreich",
          data: {
            successText: `${response.ordersUpdated} Bestellung(en) wurden exportiert und automatisch auf "Erfüllt" gesetzt.`,
          },
        })
      );

      setSelectedOrderIds((prev) =>
        prev.filter((id) => !exportableIds.includes(id))
      );
      refetchAdminOrders();
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "SEPA Export fehlgeschlagen",
          data: { errorText: error?.data?.message || "Unbekannter Fehler" },
        })
      );
    }
  };

  const handleDownloadReport = async (format) => {
    try {
      const blob = await downloadShopMonthlyReport({
        month: adminMonth,
        format,
      }).unwrap();
      const ext = format === "pdf" ? "pdf" : "csv";
      const ct =
        format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8";
      const downloadBlob = new Blob([blob], { type: ct });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(downloadBlob);
      a.download = `shop-report-${adminMonth}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      refetchMonthlyReport();
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Report Download fehlgeschlagen",
          data: { errorText: error?.data?.message || "Unbekannter Fehler" },
        })
      );
    }
  };

  // ── Render ──
  return (
    <>
      <Helmet>
        <title> MA | Shop </title>
      </Helmet>

      <Container
        id="ShopPageContainer"
        maxWidth="xl"
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          overflowX: "clip",
          mb: { xs: 10 },
        }}
      >
        <Stack spacing={{ xs: 2, md: 3 }}>
          {showPageHeader ? (
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={{ xs: 1.25, md: 2 }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{ fontSize: { xs: "1.65rem", sm: "2.125rem" } }}
                >
                  Shop
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Katalog, Warenkorb und Bestellverwaltung mit SEPA-Workflow.
                </Typography>
              </Box>

              {showAdminPreviewToggle ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={memberPreview}
                      onChange={(e) => setMemberPreview(e.target.checked)}
                    />
                  }
                  label="Mitgliederansicht simulieren"
                />
              ) : null}
            </Stack>
          ) : null}

          {canManageOperations ? (
            <Alert severity="success">
              Admin-Operationen aktiv. Hauptkonto:{" "}
              {bankingAccounts?.[0]?.creditorName || "nicht gefunden"}
            </Alert>
          ) : null}

          {activeTab === "catalog" && isAdminCheckoutMode ? (
            <Paper
              variant="outlined"
              sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2.5 }}
            >
              <Stack spacing={1.75}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={1}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Bestellung für Mitglied erfassen
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Wähle das passende Mitglied aus. Die Abrechnung läuft
                      danach automatisch über den verknüpften Vertrag und kann
                      später per SEPA exportiert werden.
                    </Typography>
                  </Box>
                  <Chip
                    color={canCheckout ? "success" : "warning"}
                    variant={canCheckout ? "filled" : "outlined"}
                    label={
                      canCheckout
                        ? `Abrechnung: ${checkoutContractOwner || "Vertrag"}`
                        : "Mitglied auswählen"
                    }
                  />
                </Stack>

                <Autocomplete
                  options={adminOrderTargets}
                  loading={loadingMembers}
                  value={selectedAdminOrderTarget}
                  onChange={(_, nextValue) =>
                    setSelectedAdminOrderTargetId(nextValue?.memberId || "")
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.memberId === value.memberId
                  }
                  getOptionLabel={(option) => option.searchLabel}
                  noOptionsText={
                    hasMembersError
                      ? "Mitglieder konnten nicht geladen werden"
                      : "Keine Mitglieder mit verknüpftem Vertrag gefunden"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Mitglied für Shop-Bestellung"
                      placeholder="Name, Mitgliedsnummer oder Vertragsinhaber suchen"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Stack sx={{ py: 0.25 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {option.memberName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Abrechnung über {option.contractOwner}
                          {option.memberNumber
                            ? ` · Nr. ${option.memberNumber}`
                            : ""}
                          {option.siteName ? ` · ${option.siteName}` : ""}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                />

                {selectedAdminOrderTarget ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Bestellung für{" "}
                    <strong>{selectedAdminOrderTarget.memberName}</strong>.
                    Abrechnung über{" "}
                    <strong>{selectedAdminOrderTarget.contractOwner}</strong>
                    {selectedAdminOrderTarget.memberNumber
                      ? ` · Mitgliedsnummer ${selectedAdminOrderTarget.memberNumber}`
                      : ""}
                    .
                  </Alert>
                ) : (
                  <Alert
                    severity={hasMembersError ? "error" : "warning"}
                    sx={{ borderRadius: 2 }}
                  >
                    {hasMembersError
                      ? "Die Mitgliederliste konnte nicht geladen werden. Der Admin-Checkout ist aktuell nicht verfügbar."
                      : "Ohne Auswahl bleiben Warenkorb und Checkout deaktiviert."}
                  </Alert>
                )}
              </Stack>
            </Paper>
          ) : null}

          {showTabNavigation ? (
            <Paper
              variant="outlined"
              sx={{ borderRadius: 2, overflow: "hidden" }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.key}
                    label={tab.label}
                    value={tab.key}
                    sx={{
                      px: { xs: 1.75, sm: 2.5 },
                      minHeight: { xs: 44, sm: 48 },
                    }}
                  />
                ))}
              </Tabs>
            </Paper>
          ) : null}

          {/* ── CATALOG TAB ── */}
          {activeTab === "catalog" ? (
            isProductRoute ? (
              <ShopProductDetailPage
                product={selectedProduct}
                loadingProduct={loadingProducts}
                initialVariantKey={variantKeyFromUrl}
                onVariantChange={handleVariantChange}
                onAddToCart={handleAddToCart}
                onGoToCart={handleGoToCart}
                onBack={handleCloseProductPage}
                hasContractContext={canCheckout}
                checkoutDisabledMessage={checkoutDisabledMessage}
                checkoutContextNote={checkoutContextNote}
                isMemberCatalogView={isMemberCatalogView}
              />
            ) : (
              <ShopCatalogTab
                catalogProducts={catalogProducts}
                isMemberCatalogView={isMemberCatalogView}
                hasContractContext={canCheckout}
                loadingProducts={loadingProducts}
                loadingAccess={loadingAccess}
                onSelectProduct={handleSelectProduct}
                cartItems={cartItems}
                setCartItems={setCartItems}
                cartTotal={cartTotal}
                cartTotalItems={cartTotalItems}
                onCheckout={handleCheckout}
                creatingOrder={creatingOrder}
                variantInventoryByKey={cartVariantInventoryByKey}
                checkoutDisabledMessage={checkoutDisabledMessage}
                checkoutContextNote={checkoutContextNote}
              />
            )
          ) : null}

          {/* ── PRODUCTS TAB (Admin) ── */}
          {activeTab === "products" && canManageProducts ? (
            <ShopProductsTab
              products={products}
              loadingProducts={loadingProducts}
              onOpenCreateDialog={() => {
                setEditingProduct(null);
                setProductDialogOpen(true);
              }}
              onEdit={(product) => {
                setEditingProduct(product);
                setProductDialogOpen(true);
              }}
              onDelete={handleDeleteProduct}
              onToggleActive={handleToggleProductActive}
              onReorder={handleReorderProducts}
              deletingProduct={deletingProduct}
              togglingId={togglingProductId}
            />
          ) : null}

          {/* ── ORDERS TAB (Admin) ── */}
          {activeTab === "orders" && canManageOperations ? (
            <ShopOrdersTab
              adminOrders={adminOrders}
              loadingAdminOrders={loadingAdminOrders}
              adminOrderStats={adminOrderStats}
              adminMonth={adminMonth}
              setAdminMonth={setAdminMonth}
              orderStatusFilter={orderStatusFilter}
              setOrderStatusFilter={setOrderStatusFilter}
              selectedOrderIds={selectedOrderIds}
              setSelectedOrderIds={setSelectedOrderIds}
              onRefetch={refetchAdminOrders}
              onOrderStatusChange={handleOrderStatusChange}
              onCancelOrders={handleCancelOrders}
              onDeleteOrders={handleDeleteOrders}
              onBulkStatusChange={handleBulkStatusChange}
              onExportSepa={handleExportSepa}
              exportingSepa={exportingSepa}
              runningAdminAction={runningAdminAction}
              products={products}
            />
          ) : null}

          {/* ── REPORTS TAB (Admin) ── */}
          {activeTab === "reports" && canManageOperations ? (
            <ShopReportsTab
              adminMonth={adminMonth}
              setAdminMonth={setAdminMonth}
              monthlyReport={monthlyReport}
              loadingMonthlyReport={loadingMonthlyReport}
              onDownloadReport={handleDownloadReport}
              downloadingReport={downloadingReport}
            />
          ) : null}
        </Stack>
      </Container>

      {/* ── Checkout Dialog ── */}
      <ShopCheckoutDialog
        open={checkoutReviewOpen}
        onClose={() => {
          setCheckoutReviewOpen(false);
          setCheckoutFulfillmentType("shipping");
        }}
        cartItems={cartItems}
        cartTotal={cartTotal}
        contractOwner={checkoutContractOwner}
        checkoutMemberName={checkoutMemberName}
        onConfirm={handleConfirmCheckout}
        creatingOrder={creatingOrder}
        hasContractContext={canCheckout}
        isAdminCheckout={isAdminCheckoutMode}
        fulfillmentType={checkoutFulfillmentType}
        onFulfillmentTypeChange={setCheckoutFulfillmentType}
      />

      {/* ── Product Form Dialog ── */}
      <ProductFormDialog
        key={editingProduct?._id || "product-create"}
        open={productDialogOpen}
        onClose={() => {
          setProductDialogOpen(false);
          setEditingProduct(null);
        }}
        onSave={handleSaveProduct}
        onUploadImages={handleUploadProductImages}
        uploadingImages={uploadingImages}
        initialProduct={editingProduct}
        categoryOptions={productCategoryOptions}
        loading={creatingProduct || updatingProduct}
      />

      {/* ── Mobile Cart FAB + Drawer ── */}
      {activeTab === "catalog" ? (
        <>
          <ShopCartFab
            itemCount={cartTotalItems}
            onClick={() => setCartDrawerOpen(true)}
          />
          <ShopCartDrawer
            open={cartDrawerOpen}
            onClose={() => setCartDrawerOpen(false)}
            cartItems={cartItems}
            setCartItems={setCartItems}
            cartTotal={cartTotal}
            cartTotalItems={cartTotalItems}
            onCheckout={handleCheckout}
            creatingOrder={creatingOrder}
            hasContractContext={canCheckout}
            isMemberCatalogView={isMemberCatalogView}
            variantInventoryByKey={cartVariantInventoryByKey}
            checkoutDisabledMessage={checkoutDisabledMessage}
            checkoutContextNote={checkoutContextNote}
          />
        </>
      ) : null}
    </>
  );
}
